import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCircleIcon } from '@heroicons/react/24/solid';

export default function ProfilePage({ initialData = null }) {
  const [profile, setProfile] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';
  
  const [kycData, setKycData] = useState({
    dateOfBirth: '',
    gender: '',
    nationality: '',
    mobileNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  });
  const [documents, setDocuments] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const clearForm = () => {
    setKycData({
      dateOfBirth: '',
      gender: '',
      nationality: '',
      mobileNumber: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    });
    setDocuments({});
    setTermsAccepted(false);
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Fetch user profile
      const response = await fetch(`${API_URL}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const profileData = await response.json();
      
      // Fetch KYC status
      const kycResponse = await fetch(`${API_URL}/kyc/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!kycResponse.ok) {
        throw new Error('Failed to fetch KYC status');
      }

      const kycData = await kycResponse.json();

      // Combine profile and KYC data
      const combinedProfile = {
        name: profileData.name,
        email: profileData.email,
        phone: profileData.phone,
        address: profileData.address,
        created_at: profileData.created_at,
        kycStatus: kycData.data?.kycStatus || 'pending'
      };

      setProfile(combinedProfile);
      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (profile) {
      console.log('Profile State Updated:', profile);
    }
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setKycData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (documentType, e) => {
    const file = e.target.files[0];
    if (file) {
      console.log('File selected for:', documentType, file);
      setDocuments(prev => ({
        ...prev,
        [documentType]: file
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No token found');
      }

      // Submit KYC information first
      const kycResponse = await fetch(`${API_URL}/kyc/information`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(kycData)
      });

      if (!kycResponse.ok) {
        const errorData = await kycResponse.json();
        throw new Error(errorData.error || 'Failed to submit personal information');
      }

      // Handle document uploads
      for (const [docType, file] of Object.entries(documents)) {
        if (!file) continue;

        const formData = new FormData();
        formData.append('documentFile', file);
        formData.append('documentType', docType);
        formData.append('documentNumber', `DOC-${docType.replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}`);

        console.log('Uploading document:', {
          type: docType,
          fileName: file.name,
          number: `DOC-${docType.replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}`
        });

        const docResponse = await fetch(`${API_URL}/kyc/documents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (!docResponse.ok) {
          const errorData = await docResponse.json();
          throw new Error(errorData.error || `Failed to upload ${docType}`);
        }
      }

      // Refresh profile data
      await fetchProfile();
      
      // Clear form and show success message
      clearForm();
      alert('KYC information and documents submitted successfully');
    } catch (error) {
      console.error('Error submitting KYC:', error);
      setSubmitError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen ">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold">Error</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 rounded-lg">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
          {/* Profile Header with Logo */}
          <div className="bg-gray-900 py-4 sm:px-4 text-center">
            <div className="relative inline-block">
              {profile?.profileImage ? (
                <img
                  src={profile.profileImage}
                  alt="Profile"
                  className="rounded-full h-24 w-24 border-4 border-white shadow-lg mx-auto"
                />
              ) : (
                <UserCircleIcon className="h-24 w-24 text-white mx-auto" />
              )}
              <div className="absolute bottom-0 right-0 bg-white rounded-full p-1 shadow-lg">
                <button className="text-blue-500 hover:text-blue-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 18 18"
                    fill="currentColor"
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              </div>
            </div>
            <h2 className="mt-4 text-2xl font-bold text-white">
              {profile?.name}
            </h2>
            <p className="text-blue-100">{profile?.email}</p>
          </div>

          {/* Profile Information */}
          <div className="px-4 py-5 sm:px-6">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="text-lg font-medium text-gray-900">
                Profile Information
              </h3>
            </div>
          </div>
          <div className="px-2 py-4 sm:p-6">
            <dl className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <dt className="text-sm font-medium text-gray-500 sm:w-1/3">
                  Full name
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:w-2/3">
                  {profile?.name || 'Not provided'}
                </dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <dt className="text-sm font-medium text-gray-500 sm:w-1/3">
                  Email address
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:w-2/3">
                  {profile?.email || 'Not provided'}
                </dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <dt className="text-sm font-medium text-gray-500 sm:w-1/3">
                  Phone number
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:w-2/3">
                  {profile?.phone || 'Not provided'}
                </dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <dt className="text-sm font-medium text-gray-500 sm:w-1/3">
                  Address
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:w-2/3">
                  {profile?.address || 'Not provided'}
                </dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <dt className="text-sm font-medium text-gray-500 sm:w-1/3">
                  Member since
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:w-2/3">
                  {profile?.created_at
                    ? new Date(profile.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Not available'}
                </dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:space-x-4">
                <dt className="text-sm font-medium text-gray-500 sm:w-1/3">
                  KYC Status
                </dt>
                <dd className="mt-1 text-sm sm:mt-0 sm:w-2/3">
                  {profile?.kycStatus === 'approved' ? (
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-green-50 text-green-800 border border-green-200">
                        <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Verified
                      </span>
                      <span className="ml-2 text-sm text-gray-500">Your KYC verification is complete</span>
                    </div>
                  ) : profile?.kycStatus === 'reviewing' ? (
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-blue-50 text-blue-800 border border-blue-200">
                        <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Under Review
                      </span>
                      <span className="ml-2 text-sm text-gray-500">Your documents are being reviewed</span>
                    </div>
                  ) : profile?.kycStatus === 'rejected' ? (
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-50 text-red-800 border border-red-200">
                        <svg className="w-4 h-4 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Rejected
                      </span>
                      <span className="ml-2 text-sm text-gray-500">Your KYC verification was rejected</span>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
                        <svg className="w-4 h-4 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Pending
                      </span>
                      <span className="ml-2 text-sm text-gray-500">Please submit your documents</span>
                    </div>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Action Buttons */}
          <div className="px-4 py-5 bg-gray-50 sm:px-6 flex justify-end space-x-3">
            {/* <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-blue-500 text-sm font-medium rounded-md text-blue-500 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Edit Profile
            </button> */}
            <button
              type="button"
              onClick={() => navigate("/forgot-password")}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Change Password
            </button>
          </div>
        </div>

        {/* KYC Section - Only show if not approved */}
        {profile?.kycStatus !== 'approved' && (
          <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-6">
            <div className="px-4 py-5 sm:px-6 bg-gray-900 text-white">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold">
                    KYC Verification
                    <p className="text-sm font-normal text-gray-300 mt-1">
                      Please fill in the following details to complete your KYC
                      process
                    </p>
                  </h3>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="px-4 py-5 sm:px-6 space-y-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">
                      Personal Information
                    </h4>
                    <p className="text-sm text-gray-500">
                      Your basic personal details
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={kycData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Gender
                    </label>
                    <select
                      name="gender"
                      value={kycData.gender}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nationality
                    </label>
                    <input
                      type="text"
                      name="nationality"
                      value={kycData.nationality}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="mobileNumber"
                      value={kycData.mobileNumber}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={kycData.addressLine1}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={kycData.addressLine2}
                      onChange={handleInputChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={kycData.city}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      State
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={kycData.state}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={kycData.postalCode}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Country
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={kycData.country}
                      onChange={handleInputChange}
                      required
                      className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 transition duration-150 ease-in-out px-3 py-2"
                    />
                  </div>
                </div>
              </div>

              {/* Document Upload */}
              <div className="space-y-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 text-blue-600">
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">
                      Document Upload
                    </h4>
                    <p className="text-sm text-gray-500">
                      Upload the required documents for verification
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    "Application for PAN/TAN",
                    "Articles of Organization",
                    "EIN/Tax ID Number",
                    "Statement of the Organizer",
                    "SS4 Application",
                    "Operating Agreement",
                    "Banking Resolution",
                    "GSTN Document",
                    "Passport",
                  ].map((doc) => (
                    <div
                      key={doc}
                      className="relative border-2 border-gray-300 border-dashed rounded-lg p-3 hover:border-blue-500 transition-colors duration-150"
                    >
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {doc}
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="file"
                            onChange={(e) => handleFileChange(doc, e)}
                            accept=".jpg,.jpeg,.png,.pdf"
                            className="block w-full text-sm text-gray-500
                              file:mr-4 file:py-2 file:px-4
                              file:rounded-md file:border-0
                              file:text-sm file:font-semibold
                              file:bg-blue-50 file:text-blue-700
                              hover:file:bg-blue-100
                              transition-all duration-150"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label className="font-medium text-gray-700">
                      I agree to the{" "}
                      <a href="#" className="text-blue-600 hover:text-blue-500">
                        Terms and Conditions
                      </a>{" "}
                      and consent to the processing of my data for KYC purposes as per
                      the{" "}
                      <a href="#" className="text-blue-600 hover:text-blue-500">
                        privacy policy
                      </a>
                    </label>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="text-red-600 text-sm">{submitError}</div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                    submitting
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                  } transition-colors duration-150`}
                >
                  {submitting ? 'Submitting...' : 'Submit KYC Information'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
